import { portalFfaCatalog, type SummonKind } from "../../../data/portal-ffa-catalog";
import {
  preservedValue,
  specialResidualFromCost,
  splitDropOrbs,
} from "../economy-rules";
import type { SimEvent } from "../sim-events";
import type {
  SimEntity,
  SimMessage,
  TeamState,
  WorldState,
} from "./types";

export const C = portalFfaCatalog;

export function dist(ax: number, ay: number, bx: number, by: number): number {
  const dx = bx - ax;
  const dy = by - ay;
  return Math.hypot(dx, dy);
}

export function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}

/** Visual+sim play rect: [outerMargin, map-outerMargin]. */
export function playBounds(
  world: WorldState,
  inset = 0,
): { minX: number; minY: number; maxX: number; maxY: number } {
  const m = C.map.outerMargin + inset;
  return {
    minX: m,
    minY: m,
    maxX: world.mapW - m,
    maxY: world.mapH - m,
  };
}

export function clampToPlay(
  world: WorldState,
  x: number,
  y: number,
  inset = 0,
): { x: number; y: number } {
  const b = playBounds(world, inset);
  return {
    x: clamp(x, b.minX, b.maxX),
    y: clamp(y, b.minY, b.maxY),
  };
}

export function emit(world: WorldState, event: SimEvent): void {
  world.eventsOut.push(event);
}

export function setMessage(
  world: WorldState,
  key: string,
  params?: Record<string, string | number>,
  nameTeamId?: number,
): void {
  const msg: SimMessage = { key, params };
  if (nameTeamId !== undefined) msg.nameTeamId = nameTeamId;
  world.message = msg;
}

export function teamPreserved(team: TeamState): number {
  return preservedValue(team.ledger);
}

export function ranking(world: WorldState): TeamState[] {
  return world.teams
    .filter((t) => t.alive)
    .slice()
    .sort((a, b) => teamPreserved(b) - teamPreserved(a));
}

export function armyCount(world: WorldState, teamId: number): number {
  return world.entities.filter(
    (e) => e.teamId === teamId && e.role !== "master",
  ).length;
}

export function areAllied(world: WorldState, a: number, b: number): boolean {
  if (a === b) return false;
  const al = world.alliance;
  if (!al) return false;
  if (world.time > al.endsAt) return false;
  return al.teamIds.includes(a) && al.teamIds.includes(b);
}

export function inGrace(world: WorldState, a: number, b: number): boolean {
  const ta = world.teams[a];
  const tb = world.teams[b];
  if (!ta || !tb) return false;
  if (world.time < ta.allianceGraceUntil && world.time < tb.allianceGraceUntil)
    return true;
  const al = world.alliance;
  if (
    al &&
    world.time <= al.graceUntil &&
    al.teamIds.includes(a) &&
    al.teamIds.includes(b)
  )
    return true;
  return false;
}

export function canAttack(world: WorldState, a: number, b: number): boolean {
  if (a === b) return false;
  if (areAllied(world, a, b)) return false;
  if (inGrace(world, a, b)) return false;
  return true;
}

export function unitDef(kind: SummonKind) {
  return C.units[kind];
}

export function masterSpeed(): number {
  return C.master.speed;
}

export function spawnPoint(
  mapW: number,
  mapH: number,
  margin: number,
  index: number,
  total: number,
): { x: number; y: number } {
  const angle = (Math.PI * 2 * index) / total + 0.4;
  const rx = mapW / 2 - margin;
  const ry = mapH / 2 - margin;
  return {
    x: mapW / 2 + Math.cos(angle) * rx * 0.85,
    y: mapH / 2 + Math.sin(angle) * ry * 0.85,
  };
}

export function addEntity(
  world: WorldState,
  e: Omit<SimEntity, "id">,
): SimEntity {
  const ent: SimEntity = { ...e, id: world.nextId++ };
  world.entities.push(ent);
  return ent;
}

export function spawnMaster(
  world: WorldState,
  teamId: number,
  x: number,
  y: number,
): SimEntity {
  return addEntity(world, {
    teamId,
    role: "master",
    x,
    y,
    vx: 0,
    vy: 0,
    intentVx: 0,
    intentVy: 0,
    facing: 1,
    facingHoldUntil: 0,
    aimAngle: 0,
    animState: "idle",
    animTime: 0,
    animHoldUntil: 0,
    hp: C.master.maxHp,
    maxHp: C.master.maxHp,
    speed: masterSpeed(),
    atk: C.master.atk,
    atkRange: C.master.atkRange,
    atkCd: 0,
    leash: 0,
    radius: C.master.radius,
    cost: 0,
    color: teamId === 0 ? C.master.colorPlayer : C.master.colorBot,
    targetId: null,
    arrived: false,
    marching: false,
    combatSleepUntil: 0,
  });
}

export function spawnTroop(
  world: WorldState,
  teamId: number,
  kind: SummonKind,
  x: number,
  y: number,
): SimEntity | null {
  const team = world.teams[teamId];
  if (!team?.alive) return null;

  const def = unitDef(kind);
  const speed = masterSpeed() * def.speedMul;
  return addEntity(world, {
    teamId,
    role: kind,
    x: x + (Math.random() - 0.5) * 20,
    y: y + (Math.random() - 0.5) * 20,
    vx: 0,
    vy: 0,
    intentVx: 0,
    intentVy: 0,
    facing: 1,
    facingHoldUntil: 0,
    aimAngle: 0,
    animState: "idle",
    animTime: 0,
    animHoldUntil: 0,
    hp: def.maxHp,
    maxHp: def.maxHp,
    speed,
    atk: def.atk,
    atkRange: def.atkRange,
    atkCd: 0,
    leash: def.leash,
    radius: def.radius,
    cost: def.summonCost || def.manaEquivalent,
    color: def.color,
    targetId: null,
    arrived: false,
    marching: false,
    combatSleepUntil: 0,
  });
}

export function recalcSpecialResidual(world: WorldState, teamId: number): void {
  const team = world.teams[teamId];
  if (!team) return;
  let residual = 0;
  for (const e of world.entities) {
    if (e.teamId !== teamId) continue;
    if (e.role === "hero") {
      residual += specialResidualFromCost(
        C.units.hero.manaEquivalent,
        C.economy.specialResidualRate,
      );
    } else if (e.role === "weapon") {
      residual += specialResidualFromCost(
        C.units.weapon.manaEquivalent,
        C.economy.specialResidualRate,
      );
    }
  }
  team.ledger.specialResidualValue = residual;
}

export function dropOrbsAt(
  world: WorldState,
  x: number,
  y: number,
  total: number,
  delay: number,
): void {
  const values = splitDropOrbs(total, C.economy.maxOrbValue);
  const n = values.length;
  values.forEach((value, i) => {
    const ang = (Math.PI * 2 * i) / Math.max(1, n) + Math.random() * 0.2;
    const r = 40 + Math.random() * 70;
    world.orbs.push({
      id: world.nextId++,
      x: x + Math.cos(ang) * r,
      y: y + Math.sin(ang) * r,
      value,
      collectableAt: world.time + delay,
      expiresAt: world.time + delay + C.economy.dropExpireSec,
    });
  });
}
